/**
 * LangGraph client options for the Word pane.
 *
 * Same shape as the web app's `agent-client.ts`, minus the app-wide
 * correlation store: the pane mints a request id per user action itself
 * (`beginUserAction`) so a run's `request_id` still leads to its LangSmith
 * trace, and it hands a 401 to the caller instead of the web app's session
 * refresh (the pane's token is a 48-hour JWT with no refresh flow; an expired
 * one means signing in again).
 */
import { v4 as uuid } from "uuid";

export type UnauthorizedResolver = () => Promise<string | null>;

export interface AgentClientOptions {
  apiUrl: string;
  apiKey: null;
  defaultHeaders: Record<string, string>;
  onRequest: (url: URL | string, init: RequestInit) => RequestInit;
  callerOptions: { fetch: typeof fetch };
}

export interface AgentClientExtras {
  /** Called once per 401, before any retry is attempted. */
  onUnauthorized?: UnauthorizedResolver;
  /** Network implementation; defaults to the global `fetch`. */
  fetch?: typeof fetch;
}

let actionId = uuid();

/**
 * Start a new correlation scope: every request until the next call carries
 * this id as `x-request-id` and run metadata `request_id`.
 * @returns The new id.
 */
export const beginUserAction = (): string => {
  actionId = uuid();
  return actionId;
};

/** The current correlation id. */
export const getActionId = (): string => actionId;

const isReplayable = (init: RequestInit | undefined): boolean =>
  !(init?.body instanceof ReadableStream);

/**
 * Build the LangGraph client options for an authenticated user.
 * @param apiUrl - The Aegra base URL.
 * @param token - The user's bearer token.
 * @param extras - 401 resolver and network overrides.
 * @returns Options ready to hand to the LangGraph `Client` constructor.
 */
export const buildAgentClientOptions = (
  apiUrl: string,
  token: string,
  extras: AgentClientExtras = {}
): AgentClientOptions => {
  const authorization = `Bearer ${token}`;
  const { onUnauthorized } = extras;
  const doFetch: typeof fetch = (input, init) =>
    (extras.fetch ?? globalThis.fetch.bind(globalThis))(input, init);

  const fetchWithSessionRecovery: typeof fetch = async (input, init) => {
    const response = await doFetch(input, init);
    if (response.status !== 401 || !onUnauthorized) return response;

    let freshToken: string | null = null;
    try {
      freshToken = await onUnauthorized();
    } catch {
      return response;
    }
    if (!freshToken || !isReplayable(init)) return response;

    const headers = new Headers(init?.headers);
    headers.set("Authorization", `Bearer ${freshToken}`);
    return doFetch(input, { ...init, headers });
  };

  return {
    apiUrl,
    apiKey: null,
    defaultHeaders: { Authorization: authorization },
    onRequest: (_url, init) => {
      const headers = new Headers(init.headers);
      headers.set("Authorization", authorization);
      headers.set("x-request-id", getActionId());
      return { ...init, headers };
    },
    callerOptions: { fetch: fetchWithSessionRecovery }
  };
};

export interface RunMetadataInput {
  assistantId: string;
  page: string;
  section: string;
}

/**
 * The metadata stamped on every run: what LangSmith and PostHog group by.
 * @param input - The graph and the surface the run was sent from.
 * @returns Metadata for the run payload.
 */
export const buildRunMetadata = (
  input: RunMetadataInput
): Record<string, string> => ({
  graph_id: input.assistantId,
  page: input.page,
  section: input.section,
  request_id: getActionId()
});
